import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  TextField,
  Button,
  Box,
  Stack,
  useTheme,
  Typography,
  InputAdornment,
} from "@mui/material";
import apiService from "../apiService/ApiService";
import {
  REDIRECT_URL_RESET_PASSWORD,
  RequestResetPassword,
} from "../utils/urlConst";
import { useNavigate } from "react-router-dom";
import CommonLoader from "../components/CommonLoader";
import { useAlert } from "../contexts/AlertContext";
import AppLogoContainer from "../components/UserModule/AppLogoContainer";
import useWindowDimensions from "../hooks/useWindowDimensions";
import { useTranslation } from "react-i18next";
import { PersonOutline } from "@mui/icons-material";
import StyledTextField from "../components/StyledTextField";
import CommonTextField from "../components/CommonTextField";
import CommonButton from "../components/CommonButton";
import useResponsive from "../hooks/useResponsive";

interface FormData {
  email: string;
}

const RequestPasswordReset: React.FC = () => {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>();

  const { t } = useTranslation();
  const theme = useTheme();
  const { isMobile, isTablet } = useResponsive();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [errorMessage, setErrorMessage] = useState<string>("");
  const windowHeight = useWindowDimensions().windowHeight;

  const onSubmit = async (data: FormData) => {
    setErrorMessage("");
    setLoading(true);
    try {
      let jsondata = JSON.stringify({
        email: data.email.trim(),
        type: "app",
        redirect_url: REDIRECT_URL_RESET_PASSWORD,
      });

      const responseJson = await apiService.post<any>(
        RequestResetPassword,
        jsondata
      );

      if (
        responseJson?.hasOwnProperty("status_code") &&
        responseJson?.status_code === 1
      ) {
        showAlert(t("resetpassword.password_sent"), "success");
      } else if (
        responseJson?.hasOwnProperty("status_code") &&
        responseJson?.status_code === 0
      ) {
        setErrorMessage(t("resetpassword.email_does_not_exist"));
      } else {
        showAlert(t("common.server_error"), "error");
      }
    } catch (error) {
      showAlert(t("common.server_error"), "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        height: isMobile ? 'auto' : windowHeight,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
        overflow: "auto",
      }}
    >
      <CommonLoader isVisible={loading} />
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems="center"
        justifyContent={isMobile ? "flex-start" : "space-evenly"}
        sx={{
          width: "100%",
          height: isMobile ? "100%" : isTablet ? 'auto' : 350,
        }}
      >
        <AppLogoContainer />
        <Box
          sx={{
            width: isMobile ? "90%" : isTablet ? "50%" : "30%",
            mt: isMobile || isTablet ? "80px" : '0px',
            mb: isMobile || isTablet ? "20px" : '0px',
            alignItems: "center",
            height: 300
          }}
        >
          <form onSubmit={handleSubmit(onSubmit)} style={{ width: "100%" }}>
            <Stack spacing={"38px"}>
              <Typography
                fontFamily={"Nunito Sans Bold"}
                sx={{
                  color: "#2B2A5C",
                  alignSelf: "center",
                  fontSize: "20px",
                }}
              >
                {t("resetpassword.Reset_Password_title")}
              </Typography>
              <Controller
                name="email"
                control={control}
                defaultValue=""
                rules={{
                  required: t("login.Email_is_required"),
                  pattern: {
                    value: /^\S+@\S+$/i,
                    message: t("resetpassword.Email_is_invalid"),
                  },
                }}
                render={({ field }) => (
                  <CommonTextField
                    {...field}
                    placeholder={t("login.Email")}
                    fullWidth
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    icon={"/assets/user.svg"}
                    name={"user"}
                    onChange={(e) => field.onChange(e.target.value.trim())}
                    fontSize={"12px"}
                  />
                )}
              />
              {errorMessage && (
                <Typography
                  variant="body2"
                  sx={{
                    color: "error.main",
                    mt: 1,
                    mb: 2,
                  }}
                >
                  {errorMessage}
                </Typography>
              )}
              <CommonButton
                translationKey={"resetpassword.Reset_Password"}
                type="submit"
              />
            </Stack>
          </form>
          <Typography
            fontFamily={"Nunito Sans Regular500"}
            display={"inline"}
            tabIndex={0}
            sx={{
              mt: "25px",
              fontSize: "16px",
              textAlign: "center",
              display: "flex",
              justifyContent: "center",
              width: "100%",
              color: "#525252",
            }}
          >
            <span
              onClick={() => navigate("/login")}
              style={{
                cursor: 'pointer',
                textDecoration: "underline",
              }}
              onMouseOver={e => (e.currentTarget.style.color = theme.palette.primary.main)}
              onMouseOut={e => (e.currentTarget.style.color = '#525252')}
              onFocus={e => (e.currentTarget.style.color = theme.palette.primary.main)}
              onBlur={e => (e.currentTarget.style.color = '#525252')}
            >
              {t("resetpassword.back_to_login_button")}
            </span>
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
};

export default RequestPasswordReset;
